import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Chip } from '@mui/material';
import DataTable from '../../components/common/DataTable';
import DateRangePicker, { getDefaultDateRange, formatDateRange } from '../../components/common/DateRangePicker';
import ExportButton from '../../components/common/ExportButton';
import { useTrailerEconomics } from '../../hooks/useApi';
import type { DateRange, TrailerEconomics } from '../../types';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value: number, decimals = 0) => {
  return new Intl.NumberFormat('da-DK', {
    maximumFractionDigits: decimals,
  }).format(value);
};

const columns = [
  {
    id: 'trailerNumber',
    label: 'Køretøj',
    minWidth: 100,
  },
  {
    id: 'trailerType',
    label: 'Type',
    minWidth: 120,
    format: (value: unknown) => (
      <Chip label={String(value)} size="small" variant="outlined" />
    ),
  },
  {
    id: 'revenue',
    label: 'Omsætning',
    align: 'right' as const,
    minWidth: 120,
    format: (value: unknown) => formatCurrency(value as number),
  },
  {
    id: 'kilometers',
    label: 'Kilometer',
    align: 'right' as const,
    minWidth: 100,
    format: (value: unknown) => `${formatNumber(value as number)} km`,
  },
  {
    id: 'fuelConsumption',
    label: 'Brændstof',
    align: 'right' as const,
    minWidth: 100,
    format: (value: unknown) => `${formatNumber(value as number, 1)} L`,
  },
  {
    id: 'totalCosts',
    label: 'Omkostninger',
    align: 'right' as const,
    minWidth: 120,
    format: (value: unknown) => formatCurrency(value as number),
  },
  {
    id: 'profit',
    label: 'Profit',
    align: 'right' as const,
    minWidth: 120,
    format: (value: unknown, row: TrailerEconomics) => (
      <Typography
        component="span"
        sx={{ color: row.profit >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
      >
        {formatCurrency(value as number)}
      </Typography>
    ),
  },
  {
    id: 'revenuePerKm',
    label: 'DKK/km',
    align: 'right' as const,
    minWidth: 80,
    format: (value: unknown) => formatCurrency(value as number),
  },
  {
    id: 'routeCount',
    label: 'Ruter',
    align: 'center' as const,
    minWidth: 60,
  },
];

export default function TrailerList() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const { from, to } = formatDateRange(dateRange);

  const { data, isLoading, error } = useTrailerEconomics(from, to);

  const handleRowClick = (row: TrailerEconomics) => {
    navigate(`/trailers/${row.trailerId}`);
  };

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          Kunne ikke hente køretøjsdata. Kontroller din DORA API-forbindelse.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Vognregnskab</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <ExportButton from={from} to={to} />
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : data?.trailers ? (
        <DataTable
          columns={columns}
          data={data.trailers}
          rowKey="trailerId"
          onRowClick={handleRowClick}
          searchPlaceholder="Søg køretøj..."
        />
      ) : null}
    </Box>
  );
}
